if (!customElements.get('woodlas-craft-process')) {
  customElements.define('woodlas-craft-process', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.wl-process__track');
      this.cards = [...this.querySelectorAll('.wl-process__card:not([data-loop-copy])')];
      if (!this.track || !this.cards.length) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      this.inView = false;
      this.interacting = false;
      this.visible = new Set();
      this.loop = this.cards.length > 1;
      this.delay = Math.max(2000, Number(this.dataset.interval || 3) * 1000);
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.motion = this.querySelector('[data-motion]');
      this.querySelector('.wl-process__controls').hidden = false;
      if (this.loop) {
        // A complete cycle on either side preserves the same view at the seam.
        for (const side of ['before', 'after']) {
          const fragment = document.createDocumentFragment();
          this.cards.forEach((card, index) => {
            const copy = card.cloneNode(true);
            copy.dataset.loopCopy = side;
            copy.setAttribute('aria-hidden', 'true');
            copy.setAttribute('inert', '');
            copy.removeAttribute('data-shopify-editor-block');
            const ids = [...copy.querySelectorAll('[id]')].map(el => el.id).sort((a, b) => b.length - a.length);
            let markup = copy.innerHTML;
            ids.forEach(id => { markup = markup.split(id).join(`${id}-loop-${side}-${index}`); });
            copy.innerHTML = markup;
            fragment.append(copy);
          });
          side === 'before' ? this.track.prepend(fragment) : this.track.append(fragment);
        }
      }
      this.allCards = [...this.track.querySelectorAll('.wl-process__card')];
      this.measure();
      this.prev.disabled = this.next.disabled = !this.loop;
      this.prev.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.motion.addEventListener('click', () => { this.paused = !this.paused; if (this.paused) this.stopAnimation(); this.syncMotion(); }, options);
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
        if (!this.animation) {
          clearTimeout(this.timer); this.timer = null;
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => {
            if (!this.interacting) { this.normalize(); this.schedule(); }
          }, 160);
        }
      }, { ...options, passive: true });
      const interact = () => {
        this.interacting = true;
        this.stopAnimation();
        clearTimeout(this.timer); this.timer = null;
      };
      this.track.addEventListener('pointerdown', interact, { ...options, passive: true });
      const release = () => {
        if (!this.interacting) return;
        this.interacting = false;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => { this.normalize(); this.schedule(); }, 180);
      };
      window.addEventListener('pointerup', release, options);
      window.addEventListener('pointercancel', release, options);
      this.track.addEventListener('wheel', () => {
        this.stopAnimation(); clearTimeout(this.timer); this.timer = null;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => { this.normalize(); this.schedule(); }, 220);
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.track.addEventListener('focusin', () => { this.focused = this.track.matches(':focus-visible'); if (this.focused) this.stopAnimation(); this.syncMotion(); }, options);
      this.track.addEventListener('focusout', () => { this.focused = false; this.syncMotion(); }, options);
      this.reduced.addEventListener('change', () => this.syncMotion(), options);
      document.addEventListener('visibilitychange', () => this.syncMotion(), options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(item => item === event.target || item.contains(event.target));
        if (index < 0) return;
        this.editorSelected = true; this.stopAnimation();
        this.jump(this.base + index * this.step); this.syncMotion();
      }, options);
      document.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.syncMotion(); }, options);
      this.sectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.syncMotion();
      }, { threshold: 0 });
      this.sectionObserver.observe(this);
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? this.visible.add(entry.target) : this.visible.delete(entry.target));
        this.syncMotion();
      }, { threshold: 0 });
      this.allCards.forEach(card => this.observer.observe(card));
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.track);
      this.syncMotion(); this.update();
    }
    measure() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = this.cards[0].getBoundingClientRect().width + gap;
      if (!step || Math.abs(step - (this.step || 0)) < .1) return;
      // Native scroll snapping may move scrollLeft before ResizeObserver fires.
      // Keep the settled logical card when rotating a device or resizing a window.
      const index = this.settledIndex || 0;
      this.stopAnimation();
      this.step = step;
      this.cycle = this.cards.length * step;
      // Symmetric mobile track padding centers this card at the same cycle offset.
      this.base = this.loop ? this.cycle : 0;
      this.jump(this.base + index * step);
      this.normalize(); this.update(); this.schedule();
    }
    jump(left) {
      this.track.classList.add('is-repositioning');
      this.track.scrollLeft = left;
      // Commit without scroll snapping, then restore native swipe snapping.
      this.track.getBoundingClientRect();
      this.track.classList.remove('is-repositioning');
    }
    normalize() {
      if (!this.loop || this.animation || this.interacting) return;
      const left = this.track.scrollLeft;
      if (left < this.base - 1 || left >= this.base + this.cycle - 1) {
        this.jump(this.base + ((left - this.base) % this.cycle + this.cycle) % this.cycle);
      }
      this.update();
      this.settledIndex = ((Math.round((this.track.scrollLeft - this.base) / this.step) % this.cards.length) + this.cards.length) % this.cards.length;
    }
    canAuto() {
      return this.loop && this.dataset.autoplay !== 'false' && this.inView && !document.hidden &&
        !this.paused && !this.reduced.matches && !this.interacting && !this.focused && !this.editorSelected;
    }
    schedule() {
      clearTimeout(this.timer); this.timer = null;
      if (this.canAuto() && !this.animation) this.timer = setTimeout(() => this.move(1), this.delay);
    }
    stopAnimation() {
      cancelAnimationFrame(this.animationFrame);
      this.animation = null;
      this.track?.classList.remove('is-moving');
    }
    move(direction) {
      if (!this.loop || !this.inView || document.hidden) return;
      clearTimeout(this.timer); this.timer = null; clearTimeout(this.settleTimer);
      this.stopAnimation(); this.normalize();
      const start = this.track.scrollLeft;
      const target = this.base + (Math.round((start - this.base) / this.step) + direction) * this.step;
      if (this.reduced.matches) { this.jump(target); this.normalize(); return; }
      this.track.classList.add('is-moving');
      const animation = { start, target, time: performance.now() };
      this.animation = animation;
      const tick = now => {
        if (this.animation !== animation) return;
        const progress = Math.min(1, (now - animation.time) / 720);
        const eased = progress < .5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
        this.track.scrollLeft = start + (target - start) * eased;
        this.update();
        if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
        else { this.stopAnimation(); this.normalize(); this.schedule(); }
      };
      this.animationFrame = requestAnimationFrame(tick);
    }
    update() {
      const position = this.querySelector('[data-position]');
      if (!position || !this.step) return;
      const count = this.cards.length;
      const index = ((Math.round((this.track.scrollLeft - this.base) / this.step) % count) + count) % count;
      position.textContent = String(index + 1).padStart(2, '0');
      this.style.setProperty('--progress', `${(index + 1) / count * 100}%`);
    }
    syncMotion() {
      const unavailable = this.reduced.matches || document.hidden || !this.inView;
      this.motion.hidden = this.reduced.matches || (this.dataset.animate !== 'true' && this.dataset.autoplay === 'false');
      this.motion.setAttribute('aria-pressed', String(this.paused));
      this.motion.setAttribute('aria-label', this.paused ? 'Play carousel and illustration animations' : 'Pause carousel and illustration animations');
      this.querySelector('[data-motion-label]').textContent = this.paused ? 'Play' : 'Pause';
      this.querySelector('[data-motion-symbol]').textContent = this.paused ? '▷' : 'Ⅱ';
      this.allCards.forEach(card => card.classList.toggle('is-paused', unavailable || this.paused || this.dataset.animate !== 'true' || !this.visible.has(card)));
      if (unavailable) this.stopAnimation();
      if (unavailable || this.paused || this.focused || this.editorSelected) { clearTimeout(this.timer); this.timer = null; }
      else if (!this.timer && !this.animation) this.schedule();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.sectionObserver?.disconnect(); this.resize?.disconnect();
      clearTimeout(this.timer); this.timer = null; clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.frame); this.stopAnimation();
      this.querySelectorAll('[data-loop-copy]').forEach(card => card.remove());
      this.step = 0; this.timer = null;
    }
  });
}
